import React, { useEffect, useState } from "react";
import { makeStyles } from "@mui/styles";
import {
  TextField,
  Autocomplete,
  Button,
  FormControl,
  InputAdornment,
} from "@mui/material";
import Colors from "../../theme/colors.json";
import DeleteIcon from "@mui/icons-material/Delete";
import { useSelector } from "react-redux";
import {
  CONFIRMATION_CONTENT,
  CONFIRMATION_TITLE,
  USER_GROUPS,
} from "../../constants/constants";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import { styled, lighten, darken } from "@mui/system";

const useStyles = makeStyles((theme) => ({
  insuranceCompanytable: {
    width: "100%",
    borderCollapse: "collapse",
    border: `1px solid ${Colors.BORDER}`,
    marginBottom: "20px",
    "& th, & td": {
      border: `1px solid ${Colors.BORDER}`,
      padding: "8px",
      textAlign: "left",
    },
    background: Colors.WHITE_BACKGROUND,
    borderRadius: "0.6rem",
  },
  insuranceCompanytableHeading: {
    color: theme.palette.secondary.main,
    fontSize: theme.fontSizes.MEDIUM,
    fontWeight: 600,
  },
  validationContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  tableTextFieldStyles: {
    fontSize: theme.fontSizes.SMALL,
  },
  validationAction: {
    pointerEvents: "none",
  },

  insuranceCompanyNameField: {
    minWidth: 180,
    "& .MuiAutocomplete-inputRoot": {
      minHeight: 30, // Change height
    },
    "& .MuiInputBase-input": {
      fontSize: 12, // Change font size
    },
  },
  commonBtnStyles: {
    background: Colors.WHITE_BACKGROUND,
    textTransform: "capitalize",
  },
  insuranceCompanyTableHeadingCell: {
    fontSize: theme.fontSizes.SMALL,
    fontWeight: 600,
  },
}));

const GroupHeader = styled("div")(({ theme }) => ({
  position: "sticky",
  top: "-8px",
  padding: "4px 10px",
  color: theme.palette.primary.main,
  backgroundColor:
    theme.palette.mode === "light"
      ? lighten(theme.palette.primary.light, 0.85)
      : darken(theme.palette.primary.main, 0.8),
}));

const GroupItems = styled("ul")({
  padding: 0,
});

const InsuranceCompanyDetails = ({
  insuranceCompanyDetails,
  setInsuranceCompanyDetails,
  insuranceProviders,
}) => {
  const classes = useStyles();
  const { proposalDataRedux } = useSelector((state: any) => ({
    proposalDataRedux: state.proposalData,
  }));
  const { userGroup } = useSelector((state: any) => ({
    userGroup: state.userGroup,
  }));
  const [tableData, setTableData] = useState(
    proposalDataRedux?.formData?.policyManagementAndCustomerCare
      ?.insuranceCompanyDetails?.insuranceCompanyDetails || [
      {
        id: Math.random(),
        insurerType: "Exclusive Insurer / Lead Insurer",
        insuranceCompanyName:
          proposalDataRedux?.formData?.policyManagementAndCustomerCare
            ?.prePolicyPlacementData?.leadInsurer || "",
        branchName:
          proposalDataRedux?.formData?.policyManagementAndCustomerCare
            ?.prePolicyPlacementData?.insurerBranch || "",
        sharePercentage: 100,
        premiumAmountBeforeTax:
          proposalDataRedux?.formData?.policyManagementAndCustomerCare
            ?.prePolicyPlacementData?.netPremiumPaid || 0,
      },
    ]
  );

  const [sharePercentage, setSharePercentage] = useState(0);
  const [premiumAmountBeforeTax, setPremiumAmountBeforeTax] = useState(0);

  const [validation, setValidation] = useState(
    proposalDataRedux?.formData?.policyManagementAndCustomerCare
      ?.insuranceCompanyDetails?.validation || false
  );

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const handleYes = () => {
    setConfirmDialogOpen(false);
    setValidation(true);
  };

  const handleNo = () => {
    setConfirmDialogOpen(false);
  };

  const addRow = () => {
    const newRow = {
      id: Math.random(),
      insurerType: "",
      insuranceCompanyName: "",
      branchName: "",
      sharePercentage: 0,
      premiumAmountBeforeTax: 0,
    };
    setTableData((prevData) => [...prevData, newRow]);
  };

  const deleteRow = (id) => {
    setTableData((prevData) => prevData.filter((row) => row.id !== id));
  };

  const handleInputChange = (id, field, value) => {
    setTableData((prevData) =>
      prevData.map((row) =>
        row.id === id
          ? {
              ...row,
              [field]: value,
              ["premiumAmountBeforeTax"]:
                ((parseFloat(value) || 0) *
                  (proposalDataRedux?.formData?.policyManagementAndCustomerCare
                    ?.prePolicyPlacementData?.netPremiumPaid || 0)) /
                100,
            }
          : row
      )
    );
  };

  const calculateTotal = (field) => {
    return tableData.reduce((total, row) => {
      const value = parseFloat(row[field]) || 0;
      return total + value;
    }, 0);
  };

  const getInsuranceProviderObject = (companyId) => {
    const defaultProvider = {
      companyId: "",
      insuranceCategory: "",
      insuranceCompanyName: "",
    };

    return (
      insuranceProviders?.find((item) => item.companyId === companyId) ||
      defaultProvider
    );
  };

  useEffect(() => {
    setSharePercentage(calculateTotal("sharePercentage"));
    setPremiumAmountBeforeTax(calculateTotal("premiumAmountBeforeTax"));
    let data = {
      insuranceCompanyDetails: tableData,
      totalSharePercentage: calculateTotal("sharePercentage"),
      totalPremiumAmountBeforeTax: calculateTotal("premiumAmountBeforeTax"),
      validation: validation,
    };
    setInsuranceCompanyDetails(data);
  }, [tableData, validation]);

  return (
    <div>
      <ConfirmationDialog
        open={confirmDialogOpen}
        setOpen={setConfirmDialogOpen}
        title={CONFIRMATION_TITLE}
        content={CONFIRMATION_CONTENT}
        handleYes={handleYes}
        handleNo={handleNo}
      />
      <table
        className={`${classes.insuranceCompanytable} ${
          validation ? classes.validationAction : ""
        }`}
      >
        <thead>
          <tr>
            <th colSpan={6} className={classes.insuranceCompanytableHeading}>
              Insurance Company Details
            </th>
          </tr>
          <tr>
            <th className={classes.insuranceCompanyTableHeadingCell}>
              Insurer Type
            </th>
            <th className={classes.insuranceCompanyTableHeadingCell}>
              Insurance Company Name
            </th>
            <th className={classes.insuranceCompanyTableHeadingCell}>
              Branch Name
            </th>
            <th className={classes.insuranceCompanyTableHeadingCell}>
              % of Share
            </th>
            <th className={classes.insuranceCompanyTableHeadingCell}>
              Premium Amount before Tax
            </th>
            <th className={classes.insuranceCompanyTableHeadingCell}>
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {tableData.map((row, index) => (
            <tr key={row.id}>
              <td>
                <TextField
                  type="text"
                  value={row.insurerType}
                  name="insurerType"
                  onChange={(e) =>
                    handleInputChange(row.id, e.target.name, e.target.value)
                  }
                  InputProps={{
                    className: classes.tableTextFieldStyles,
                  }}
                  size="small"
                />
              </td>
              <td>
                <FormControl>
                  <Autocomplete
                    fullWidth
                    disableClearable
                    className={classes.insuranceCompanyNameField}
                    id="insuranceCompanyName-autocomplete"
                    options={insuranceProviders}
                    getOptionLabel={(option: any) => option.companyId || ""}
                    groupBy={(option: any) => option.insuranceCategory}
                    value={getInsuranceProviderObject(
                      row?.insuranceCompanyName
                    )}
                    onChange={(_, newValue) =>
                      handleInputChange(
                        row.id,
                        "insuranceCompanyName",
                        newValue?.companyId
                      )
                    }
                    renderInput={(params) => (
                      <TextField {...params} size="small" />
                    )}
                    renderGroup={(params) => (
                      <li key={params.key}>
                        <GroupHeader>{params.group}</GroupHeader>
                        <GroupItems>{params.children}</GroupItems>
                      </li>
                    )}
                  />
                </FormControl>
                {/* <TextField
                  type="text"
                  value={row.brokingCompanyName}
                  name="brokingCompanyName"
                  onChange={(e) =>
                    handleInputChange(row.id, e.target.name, e.target.value)
                  }
                  InputProps={{
                    className: classes.tableTextFieldStyles,
                  }}
                  size="small"
                /> */}
              </td>
              <td>
                <TextField
                  type="text"
                  value={row.branchName}
                  name="branchName"
                  onChange={(e) =>
                    handleInputChange(row.id, e.target.name, e.target.value)
                  }
                  InputProps={{
                    className: classes.tableTextFieldStyles,
                  }}
                  size="small"
                />
              </td>
              <td>
                <TextField
                  type="number"
                  name="sharePercentage"
                  value={row.sharePercentage}
                  onChange={(e) =>
                    handleInputChange(row.id, e.target.name, e.target.value)
                  }
                  size="small"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">%</InputAdornment>
                    ),
                    className: classes.tableTextFieldStyles,
                  }}
                />
              </td>
              <td>
                <TextField
                  type="number"
                  name="premiumAmountBeforeTax"
                  disabled
                  value={row.premiumAmountBeforeTax}
                  // onChange={(e) =>
                  //   handleInputChange(row.id, e.target.name, e.target.value)
                  // }
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">₹</InputAdornment>
                    ),
                    className: classes.tableTextFieldStyles,
                  }}
                />
              </td>
              <td>
                {tableData.length > 1 && (
                  <Button
                    onClick={() => deleteRow(row.id)}
                    color="secondary"
                    size="small"
                  >
                    <DeleteIcon />
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={3}>Total</td>
            <td>
              <TextField
                type="number"
                value={sharePercentage}
                disabled
                size="small"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">%</InputAdornment>
                  ),
                  className: classes.tableTextFieldStyles,
                }}
              />
            </td>
            <td>
              <TextField
                type="number"
                value={premiumAmountBeforeTax}
                disabled
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">₹</InputAdornment>
                  ),
                  className: classes.tableTextFieldStyles,
                }}
              />
            </td>
          </tr>
        </tfoot>
      </table>

      <div className={classes.validationContainer}>
        <Button
          className={classes.commonBtnStyles}
          variant="outlined"
          color="primary"
          onClick={addRow}
          size="small"
          disabled={validation === true ? true : false}
        >
          Add New Row
        </Button>

        <Button
          className={classes.commonBtnStyles}
          variant="outlined"
          size="small"
          color="primary"
          onClick={() => setConfirmDialogOpen(true)}
          disabled={validation === true ? true : false}
        >
          Validation
        </Button>
        {userGroup === USER_GROUPS.ADMIN ? (
          <Button
            className={classes.commonBtnStyles}
            onClick={() => setValidation(false)}
            variant="outlined"
            size="small"
            disabled={!validation}
          >
            Revalidation
          </Button>
        ) : null}
      </div>
    </div>
  );
};

export default InsuranceCompanyDetails;
